import type { ReactNode } from "@bestowinc/tinkertoy-shell-deps/react";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "@bestowinc/tinkertoy-shell-deps/react";
import { Helmet } from "react-helmet";
import type {
  AnyView,
  ContentInstancePosition,
  ModuleDataMap,
  ModuleID,
  ModulesConfig,
  ResolvedNavigationTarget,
  VersionString,
  ViewModuleRef,
} from "@bestowinc/tinkertoy-core";
import {
  contentPosition,
  layoutInstanceKey,
  moduleInstanceKey,
  moduleRef,
  readModuleData,
  slotPosition,
  slotRefs,
} from "@bestowinc/tinkertoy-core";

import type { ModuleLoader } from "../module-loader";
import { ModuleRenderer } from "../module-renderer/module-renderer";
import { getModuleLoadPromise } from "../module-renderer/module-read-resource";
import {
  beginV2ReadinessTracking,
  cancelV2ReadinessTracking,
} from "../performance-telemetry";
import { useTinkertoy } from "../router/use-tinkertoy";
import { useShellRuntime } from "../shell-runtime";
import type { ViewIdentity } from "../view-identity";
import { ViewIdentityProvider } from "../view-identity";
import { buildInteractionHandlers } from "./build-interaction-handlers";
import { collectAllModuleLoadKeys } from "./collect-module-prefetch";
import { getModuleVersion, resolveModuleProps } from "./resolve-module-refs";

// ============================================================================
// View State Reducer
// ============================================================================

type ViewStateAction<T> = { type: "assign"; payload: Partial<T> };

/**
 * Reducer-based view-state management mirroring V1's `view-state-reducer`:
 * `T` is inferred from the initial state, so callers don't have to repeat
 * the view's state generic and the renderer doesn't need {@link View}-specific
 * generics to keep the merge well-typed.
 *
 * Stateless views (defined via {@link defineView}) inject `context: () => undefined`,
 * so `viewState` starts as `undefined`. If a module on such a view calls
 * `setViewState`, we no-op rather than coerce `undefined` into an object via
 * spread — that would silently corrupt state from `undefined` to a partial
 * object. Same defense for `null` / non-object state.
 */
export function viewStateReducer<T>(state: T, action: ViewStateAction<T>): T {
  if (action.type === "assign") {
    if (state === null || state === undefined || typeof state !== "object") {
      return state;
    }
    return { ...state, ...action.payload };
  }
  return state;
}

// ============================================================================
// ViewRenderer
// ============================================================================

export type ViewRendererProps = {
  /** Live view definition (typically loaded from a view bundle on the CDN). */
  view: AnyView;
  /**
   * Exact loader-resolved version of the view bundle being rendered.
   * Forwarded to module-action and view-interaction calls so the server can
   * cross-check the request against the manifest pinned for this view.
   */
  viewVersion: VersionString;
  /** Server data fed into `view.context` and exposed as `data` in module callbacks. */
  viewData: unknown;
  moduleDataMap: ModuleDataMap;
  layoutModuleRef: ViewModuleRef;
  resolvedModuleVersions: Record<ModuleID, VersionString>;
  /**
   * Pre-resolved navigation targets emitted by the server for the current
   * view's navigation interactions, keyed by interaction name. Threaded
   * into {@link buildInteractionHandlers} so client-side navigation never
   * needs the flow / branch / view registry to look up a target.
   */
  resolvedInteractions: Record<string, ResolvedNavigationTarget>;
  /**
   * The current request's matched path params, as emitted by the loader.
   * Used by navigation handlers as the default-fill for `:name` tokens in
   * route templates; caller-supplied params win on collision.
   */
  currentPathParams: Record<string, string>;
  loader: ModuleLoader;
  /** Shown while any module in this view is still loading (passed to each {@link ModuleRenderer}). */
  loadingFallback?: ReactNode;
  /**
   * Rendered inside the layout in place of the view's content — the shell's
   * loading module while the next view loads. The layout stays mounted. Only
   * `undefined` renders the view's content; `null` leaves the layout empty.
   */
  loadingContent?: ReactNode;
};

/**
 * V2 counterpart to V1's `ViewComposer`: view state, interaction wiring, layout
 * wrap, and recursive module (including branch slots) rendering.
 *
 * Preloads all module bundles for the view in parallel from a commit-phase
 * effect (`void getModuleLoadPromise` per key); each `ModuleRenderer` then
 * subscribes to the same cached promise. Prefetching is deliberately kept out
 * of render so that aborted concurrent renders don't kick off unnecessary
 * network work.
 *
 * Intentionally non-generic over the view (mirrors V1's `AnyView` approach): type safety for
 * `viewState`, `data`, and `interactions` is enforced at module callback sites via the
 * `moduleRef()` helper's generics on `defineView`, not at this renderer.
 *
 * @remarks `viewState` is derived from `view.context(viewData)` exactly once on mount
 * (matches V1's `ViewComposer`). View transitions must remount this component —
 * `TinkertoyShell` keys it by `viewId@viewVersion`.
 */
export function ViewRenderer({
  view,
  viewVersion,
  viewData,
  moduleDataMap,
  layoutModuleRef,
  resolvedModuleVersions,
  resolvedInteractions,
  currentPathParams,
  loader,
  loadingFallback,
  loadingContent,
}: ViewRendererProps): ReactNode {
  const [viewState, dispatch] = useReducer(viewStateReducer, viewData, view.context);

  const { navigate, goBack, revalidate, routeState } = useTinkertoy();
  const { apiBaseUrl } = useShellRuntime();

  // When the view declares its own title, override the shell's base-title
  // <Helmet> with the composed "<view> | <base>" (innermost <Helmet> wins).
  // Views without a title leave the shell's base title in place.
  const composedTitle = view.meta?.title
    ? `${view.meta.title} | ${routeState.flowMeta.title}`
    : undefined;

  // Stable identity ref so consumers' useMemo deps (e.g. useClientActionMap)
  // don't fire on every render of the parent. Only changes when one of the
  // primitive identity fields changes.
  const viewIdentity = useMemo<ViewIdentity>(
    () => ({ viewId: view.id, viewVersion }),
    [view.id, viewVersion],
  );

  const setViewStatePartial = useCallback((payload: Partial<typeof viewState>) => {
    dispatch({ type: "assign", payload });
  }, []);

  const interactionHandlers = useMemo(
    () =>
      buildInteractionHandlers(view.interactions, {
        viewId: view.id,
        viewVersion,
        apiBaseUrl,
        resolvedInteractions,
        currentPathParams,
        navigate,
        goBack,
        revalidate,
      }),
    [
      view.interactions,
      view.id,
      viewVersion,
      apiBaseUrl,
      resolvedInteractions,
      currentPathParams,
      navigate,
      goBack,
      revalidate,
    ],
  );

  const config = useMemo(
    (): ModulesConfig => ({
      interactions: interactionHandlers,
      data: viewData,
      viewState,
      setViewState: setViewStatePartial,
    }),
    [interactionHandlers, viewData, viewState, setViewStatePartial],
  );

  const { layoutRef, contentRefs, allModuleLoadKeys } = useMemo(() => {
    const layout = view.layoutOverride?.(moduleRef) ?? layoutModuleRef;
    const content = view.modules(moduleRef);
    return {
      layoutRef: layout,
      contentRefs: content,
      allModuleLoadKeys: collectAllModuleLoadKeys(
        layout,
        content,
        resolvedModuleVersions,
      ),
    };
  }, [view, layoutModuleRef, resolvedModuleVersions]);

  // Snapshot required modules once per view identity, so readiness tracking
  // doesn't restart or cancel when the loader data for the same view is
  // refreshed.
  const readinessModuleLoadKeys = useMemo(
    () => allModuleLoadKeys,
    [view.id, viewVersion],
  );

  useEffect(() => {
    beginV2ReadinessTracking({
      viewId: view.id,
      viewVersion,
      requiredModules: readinessModuleLoadKeys,
    });

    return () => {
      cancelV2ReadinessTracking(view.id, viewVersion);
    };
  }, [readinessModuleLoadKeys, view.id, viewVersion]);

  // Prefetch every module bundle this view will render in parallel. Done in a
  // commit-phase effect (not during render) so that aborted concurrent renders
  // can't trigger network work for a view we may never mount. `ModuleRenderer`
  // subscribes to the same cached promise via `getModuleLoadPromise`, so the
  // load it kicks off (also from an effect) collapses into this one when the
  // bundle is already in flight.
  useEffect(() => {
    for (const { moduleId, version } of allModuleLoadKeys) {
      void getModuleLoadPromise(loader, moduleId, version);
    }
  }, [allModuleLoadKeys, loader]);

  const layoutKey = layoutInstanceKey(layoutRef.moduleId);

  return (
    <ViewIdentityProvider value={viewIdentity}>
      {composedTitle && (
        <Helmet>
          <title>{composedTitle}</title>
        </Helmet>
      )}
      <ModuleRenderer
        moduleId={layoutRef.moduleId}
        instanceKey={layoutKey}
        version={getModuleVersion(layoutRef, resolvedModuleVersions)}
        data={readModuleData(moduleDataMap, layoutKey, layoutRef.moduleId)}
        loader={loader}
        loadingFallback={loadingFallback}
        props={resolveModuleProps(layoutRef, config)}
      >
        {loadingContent !== undefined
          ? loadingContent
          : contentRefs.map((ref, i) => (
              <Fragment key={`${String(ref.moduleId)}-${i}`}>
                {renderModuleSubtree(
                  ref,
                  contentPosition(i),
                  config,
                  resolvedModuleVersions,
                  moduleDataMap,
                  { loader, loadingFallback },
                )}
              </Fragment>
            ))}
      </ModuleRenderer>
    </ViewIdentityProvider>
  );
}

type SubtreeCtx = {
  loader: ModuleLoader;
  loadingFallback?: ReactNode;
};

/**
 * Render a module ref and, for branch modules, its slots (`ref.slots`, read
 * through core's `slotRefs` — the same walk the server keys data and actions
 * by). Every ref is keyed by its position: `2`, `2/left`, `2/left/inner`.
 */
function renderModuleSubtree(
  ref: ViewModuleRef,
  position: ContentInstancePosition,
  config: ModulesConfig,
  resolvedModuleVersions: Record<ModuleID, VersionString>,
  moduleDataMap: ModuleDataMap,
  ctx: SubtreeCtx,
): ReactNode {
  const { loader, loadingFallback } = ctx;
  const instanceKey = moduleInstanceKey(position, ref.moduleId);
  const childRefs = slotRefs(ref, position);
  const slots =
    childRefs.length > 0
      ? Object.fromEntries(
          childRefs.map(([name, slotRef]) => [
            name,
            renderModuleSubtree(
              slotRef,
              slotPosition(position, name),
              config,
              resolvedModuleVersions,
              moduleDataMap,
              ctx,
            ),
          ]),
        )
      : undefined;

  return (
    <ModuleRenderer
      moduleId={ref.moduleId}
      instanceKey={instanceKey}
      version={getModuleVersion(ref, resolvedModuleVersions)}
      loader={loader}
      data={readModuleData(moduleDataMap, instanceKey, ref.moduleId)}
      loadingFallback={loadingFallback}
      props={resolveModuleProps(ref, config)}
      slots={slots}
    />
  );
}
